import { ViewController } from 'elc-service';
import { observable, computed, action } from 'mobx';
import { RatingSelectedUseCase } from '../use-cases/RatingSelectedUseCase';
import { ReadReviewsClickedUseCase } from '../use-cases/ReadReviewsClickedUseCase';
import { WriteReviewClickedUseCase } from '../use-cases/WriteReviewClickedUseCase';
import { DEFAULT_NUMBER_OF_STARS } from '../constants/ProductReview';
import { RatingsLoadedUseCase } from '../use-cases/RatingsLoadedUseCase';
import { ProductRepository } from '../repositories/ProductRepository';
import { NO_VALUE } from '../constants/NoValue';
import { AVERAGE_RATING } from '../constants/PowerReviews';

export class ProductRatingViewController extends ViewController {
    @observable public selectedRatingIndex: number = -1;
    @observable public productRepository: ProductRepository;
    @observable public productId: string;
    @observable private numberOfStars: number = DEFAULT_NUMBER_OF_STARS;
    @observable public numberOfReviews: number;

    constructor(productRepository: ProductRepository) {
        super();
        this.productRepository = productRepository;
    }

    @computed
    public get data() {
        return {
            selectedRatingIndex: this.selectedRatingIndex,
            percentageRating: this.percentageRating,
            onSelectRating: this.onSelectRating,
            onReadReviewsClick: this.onReadReviewsClick,
            onWriteReviewClick: this.onWriteReviewClick,
            isLoaded: this.isLoaded,
            numberOfReviews: this.numberOfReviewsValue,
            averageRating: this.averageRating
        };
    }

    public get product() {
        return this.productRepository.getProductById(this.productId);
    }

    @computed
    public get numberOfReviewsValue(): number {
        return this.product && this.product.reviews && this.product.reviews.numberOfReviews;
    }

    @computed
    public get averageRating() {
        return this.product && this.product.reviews && this.product.reviews.averageRating;
    }

    @action
    public loaded = async (props: { productId: string; numberOfStars: number }) => {
        this.isLoading = true;
        const { numberOfStars, productId } = props;
        this.numberOfStars = numberOfStars;
        this.productId = productId;
        const ratingsLoadedUseCase = new RatingsLoadedUseCase(this.productId);
        await ratingsLoadedUseCase.execute();
        this.isLoading = false;
    };

    @computed
    public get isLoaded() {
        return this.productId && this.product && this.product.reviews ? true : false;
    }

    @computed
    private get percentageRating() {
        const defaultVal = undefined;
        const { reviews } = this.product || {};
        if (reviews && AVERAGE_RATING in reviews) {
            const { averageRating } = reviews;

            return averageRating === NO_VALUE
                ? defaultVal
                : (averageRating * 100) / this.numberOfStars;
        }

        return defaultVal;
    }

    @action
    public onSelectRating = (index: number) => {
        this.selectedRatingIndex = index;

        const ratingSelectedUseCase = new RatingSelectedUseCase(index);
        ratingSelectedUseCase.execute();
    };

    @action
    public onReadReviewsClick = () => {
        const readReviewsClickedUseCase = new ReadReviewsClickedUseCase();
        readReviewsClickedUseCase.execute();
    };

    @action
    public onWriteReviewClick = () => {
        const writeReviewClickedUseCase = new WriteReviewClickedUseCase();
        writeReviewClickedUseCase.execute();
    };

    @action
    public setRating = (index: number) => {
        this.numberOfReviews = this.numberOfReviews + 1;
    };
}
